import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import {
  StyledCellValue,
  StyledEmptyState,
  StyledItemsTableFooter,
  StyledItemsTableFrame,
  StyledItemsTableHeader,
  StyledItemsTableScroll,
  StyledModeCell,
  StyledOrderItemsColGroup,
  StyledOrderItemsTable,
  StyledOrderItemsTd,
  StyledOrderItemsTh,
  StyledOrderItemsThead,
  StyledOrderItemsTr,
  StyledOrderItemsTbody,
  StyledProductCell,
  StyledProductImage,
  StyledProductName,
  StyledWeightCell,
} from "./style";
import {
  getItemStatusBadgeSx,
  mapOrderDetailLine,
  summarizeOrderLines,
  formatOrderWeight,
} from "./orderItemDisplay";

/** Shared column widths — wider Mode & Status so labels/badge do not overlap */
const COL_WIDTHS = ["25%", "8%", "12%", "20%", "35%"];

function ItemsTableColGroup() {
  return (
    <StyledOrderItemsColGroup>
      {COL_WIDTHS.map((w, i) => (
        <col key={i} style={{ width: w }} />
      ))}
    </StyledOrderItemsColGroup>
  );
}

function ItemsTableHeaderRow() {
  return (
    <StyledOrderItemsThead>
      <tr>
        <StyledOrderItemsTh $column="product">Product</StyledOrderItemsTh>
        <StyledOrderItemsTh $column="qty" $align="center">
          Qty
        </StyledOrderItemsTh>
        <StyledOrderItemsTh $column="weight" $align="right">
          Weight
        </StyledOrderItemsTh>
        <StyledOrderItemsTh $column="mode">Mode</StyledOrderItemsTh>
        <StyledOrderItemsTh $column="status">Status</StyledOrderItemsTh>
      </tr>
    </StyledOrderItemsThead>
  );
}

export default function OrderItemsTable({ orderDetails = [], compact = false, hidePrices = false, orderStatus = null }) {
  void hidePrices;

  const navigate = useNavigate();
  const location = useLocation();

  const handleProductClick = (itemId) => {
    if (!itemId) return;
    const path = location.pathname;
    if (path.startsWith("/admin")) {
      navigate(`/admin/items/${itemId}`);
    } else if (path.startsWith("/personal-shopper")) {
      navigate(`/personal-shopper/items/${itemId}`);
    } else {
      navigate(`/dashboard/items/${itemId}`);
    }
  };

  const filteredDetails = React.useMemo(() => {
    if (!Array.isArray(orderDetails)) return [];
    const paidAndBeyond = new Set(['paid', 'packing', 'shipment', 'delivered']);
    const status = String(orderStatus || '').toLowerCase();
    return orderDetails.filter((d) => {
      const itemStatus = String(d?.item?.status ?? d?.status ?? '').toLowerCase();
      // Always hide rejected items
      if (itemStatus === 'rejected') return false;
      // For paid+ orders, also hide pending items
      if (paidAndBeyond.has(status) && itemStatus === 'pending') return false;
      return true;
    });
  }, [orderDetails, orderStatus]);

  const lines = useMemo(
    () => filteredDetails.map(mapOrderDetailLine),
    [filteredDetails]
  );
  const { itemCount, totalWeightLabel } = summarizeOrderLines(lines);

  if (itemCount === 0) {
    return (
      <StyledEmptyState sx={compact ? { py: 2 } : undefined}>
        <Inventory2OutlinedIcon sx={{ fontSize: 40, mb: 0.5, opacity: 0.5 }} />
        <Box component="p" sx={{ m: 0 }}>
          No products added yet
        </Box>
      </StyledEmptyState>
    );
  }

  return (
    <Box>
      <StyledItemsTableFrame>
        <StyledItemsTableHeader>
          <StyledOrderItemsTable>
            <ItemsTableColGroup />
            <ItemsTableHeaderRow />
          </StyledOrderItemsTable>
        </StyledItemsTableHeader>

        <StyledItemsTableScroll $compact={compact}>
          <StyledOrderItemsTable>
            <ItemsTableColGroup />
            <StyledOrderItemsTbody>
              {lines.map((line, index) => (
                <StyledOrderItemsTr key={line.id || index}>
                  <StyledOrderItemsTd $column="product">
                    <StyledProductCell>
                      <StyledProductImage $compact={compact}>
                        {line.image ? (
                          <Box
                            component="img"
                            src={line.image}
                            alt=""
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <Inventory2OutlinedIcon
                            sx={{ color: "action.disabled", fontSize: compact ? 28 : 32 }}
                            aria-hidden
                          />
                        )}
                      </StyledProductImage>
                      <StyledProductName
                        component="p"
                        sx={{ m: 0 }}
                        title={line.name}
                        onClick={() => handleProductClick(line.itemId)}
                      >
                        {line.name}
                      </StyledProductName>
                    </StyledProductCell>
                  </StyledOrderItemsTd>
                  <StyledOrderItemsTd $column="qty" $align="center">
                    <StyledCellValue sx={{ textAlign: "center", display: "block" }}>
                      {line.quantity}
                    </StyledCellValue>
                  </StyledOrderItemsTd>
                  <StyledOrderItemsTd $column="weight" $align="right">
                    <StyledWeightCell component="span">
                      {formatOrderWeight(line.weight)}
                    </StyledWeightCell>
                  </StyledOrderItemsTd>
                  <StyledOrderItemsTd $column="mode">
                    <StyledModeCell component="span" display="block" title={line.mode}>
                      {line.mode}
                    </StyledModeCell>
                  </StyledOrderItemsTd>
                  <StyledOrderItemsTd $column="status">
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        overflow: "visible",
                      }}
                    >
                      <Box component="span" sx={getItemStatusBadgeSx(line.status)}>
                        {line.statusLabel}
                      </Box>
                    </Box>
                  </StyledOrderItemsTd>
                </StyledOrderItemsTr>
              ))}
            </StyledOrderItemsTbody>
          </StyledOrderItemsTable>
        </StyledItemsTableScroll>
      </StyledItemsTableFrame>

      <StyledItemsTableFooter>
        <Box component="span">
          Total items: <strong>{itemCount}</strong>
        </Box>
        <Box component="span">
          Total weight: <strong>{totalWeightLabel}</strong>
        </Box>
      </StyledItemsTableFooter>
    </Box>
  );
}
